import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Button } from "react-bootstrap";
import moment from "moment";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { formatDate } from "../../../utils/function";
import { adminGetAnalyticsList } from "../../../config/service";
import AnalyticsDetail from "./AnalyticsDetailModal";
import AnalyticsCharts from "./AnalyticsCharts";

const SearchHeader = ({
  field,
  fieldLabel,
  columnSearchRef,
  onSearch,
  searchNonce,
}) => {
  const [localValue, setLocalValue] = useState(
    columnSearchRef.current[field] || "",
  );

  useEffect(() => {
    setLocalValue(columnSearchRef.current[field] || "");
  }, [searchNonce]);

  const handleSearch = () => {
    columnSearchRef.current[field] = localValue;
    onSearch();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <strong>{fieldLabel}</strong>
      <div className="d-flex">
        <input
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search"
        />
        <button onClick={handleSearch}>🔍</button>
      </div>
    </div>
  );
};

const toIsoDate = (dateStr) => {
  if (!dateStr) return "";

  // expecting DD/MM/YYYY
  const parsed = moment(dateStr, "DD/MM/YYYY", true);

  if (!parsed.isValid()) return "";

  return parsed.toISOString(); // 👉 2024-07-08T14:30:00.148Z
};

const AnalyticsReport = () => {
  const gridApi = useRef(null);
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [eventId, setEventId] = useState("");
  const [detailModal, setDetailModal] = useState(false);
  const [showCharts, setShowCharts] = useState(false);

  const itemsPerPage = 10;

  const initialSearchFields = {
    eventId: "",
    createdAt: "",
    eventType: "",
    stepNumber: "",
    utm: "",
    ipAddress: "",
    os: "",
    browser: "",
  };

  const columnSearchRef = useRef({ ...initialSearchFields });
  const [searchNonce, setSearchNonce] = useState(0);

  const handleSearchUpdate = () => {
    setCurrentPage(1);
    setSearchNonce((n) => n + 1);
  };

  const handleResetSearch = () => {
    columnSearchRef.current = { ...initialSearchFields };
    setSearchNonce((n) => n + 1);
    setCurrentPage(1);
  };

  const handleCloseAnalyticsModal = () => setDetailModal(false);

  const getColumnDefs = useCallback(
    (screenWidth) => [
      {
        field: "eventId",
        headerName: "Event ID",
        width: screenWidth <= 991 ? 200 : 270,
        headerComponent: () => (
          <SearchHeader
            field="eventId"
            fieldLabel="Event ID"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: "Created Date",
        width: screenWidth <= 991 ? 160 : 100,
        valueFormatter: (p) => formatDate(p.value),
        headerComponent: () => (
          <SearchHeader
            field="createdAt"
            fieldLabel="Date"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "eventType",
        headerName: "Event Type",
        width: screenWidth <= 991 ? 140 : 200,
        headerComponent: () => (
          <SearchHeader
            field="eventType"
            fieldLabel="Event Type"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "stepNumber",
        headerName: "Step Number",
        width: 100,
        headerComponent: () => (
          <SearchHeader
            field="stepNumber"
            fieldLabel="Step Number"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "utm",
        headerName: "UTM",
        width: 180,
        headerComponent: () => (
          <SearchHeader
            field="utm"
            fieldLabel="UTM"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "ipAddress",
        headerName: "IP Address",
        width: 140,
        headerComponent: () => (
          <SearchHeader
            field="ipAddress"
            fieldLabel="IP Address"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "os",
        headerName: "OS",
        width: 140,
        headerComponent: () => (
          <SearchHeader
            field="os"
            fieldLabel="OS"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "browser",
        headerName: "Browser",
        width: 140,
        headerComponent: () => (
          <SearchHeader
            field="browser"
            fieldLabel="Browser"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },

      {
        field: "action",
        headerName: "Action",
        width: 120,
        pinned: screenWidth <= 991 ? "right" : null,
        cellRendererFramework: (params) => (
          <Button onClick={() => veiwAnalyticsDetail(params.data.eventId)}>
            Detail
          </Button>
        ),
      },
    ],
    [searchNonce],
  );

  const [columnDefs, setColumnDefs] = useState(() =>
    getColumnDefs(window.innerWidth),
  );

  const veiwAnalyticsDetail = (id) => {
    setEventId(id);
    setDetailModal(true);
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      editable: true,
    }),
    [],
  );

  const fetchAnalytics = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const payload = {
        page,
        perPage: itemsPerPage,
        searchFields: {
          ...columnSearchRef.current,
          createdAt: toIsoDate(columnSearchRef.current.createdAt),
        },
      };

      const response = await adminGetAnalyticsList(payload);

      const rows = response.data.data.map((d) => ({
        eventId: d.eventId,
        createdAt: d.createdAt,
        eventType: d.eventType,
        stepNumber: d.meetingId ? d.meetingId : d.stepNumber,
        utm: d.utm,
        ipAddress: d.ipAddress,
        os: d.os,
        browser: d.browser,
      }));

      setRowData(rows);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics(currentPage);
  }, [currentPage, searchNonce]);

  const onGridReady = (params) => {
    gridApi.current = params.api;
  };

  const exportCurrentPage = () => {
    gridApi.current.exportDataAsCsv({
      fileName: `analytics-${moment().format("DD-MM-YYYY-HH-mm")}.csv`,
    });
  };

  const exportAll = async () => {
    try {
      if (!gridApi.current) return;
      const response = await adminGetAnalyticsList({
        page: 1,
        perPage: 1000000,
        searchFields: {
          eventId: "",
          createdAt: "",
          eventType: "",
          stepNumber: "",
          utm: "",
          ipAddress: "",
          os: "",
          browser: "",
        },
      });

      const allRows = response.data.data.map((d) => ({
        eventId: d.eventId,
        createdAt: d.createdAt,
        eventType: d.eventType,
        stepNumber: d.stepNumber,
        utm: d.utm,
        ipAddress: d.ipAddress,
        os: d.os,
        browser: d.browser,
      }));

      const prevData = rowData;
      gridApi.current.setRowData(allRows);

      gridApi.current.exportDataAsCsv({
        fileName: `analytics-all-${moment().format("DD-MM-YYYY-HH-mm")}.csv`,
        allColumns: true,
      });

      gridApi.current.setRowData(prevData);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    }
  };

  const handleShowCharts = () => {
    setShowCharts(!showCharts);
  };

  useEffect(() => {
    const handleResize = () => {
      setColumnDefs(getColumnDefs(window.innerWidth));
    };

    setColumnDefs(getColumnDefs(window.innerWidth));

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getColumnDefs]);

  return (
    <div className="user-mangement-page">
      {detailModal && (
        <AnalyticsDetail
          handleClose={handleCloseAnalyticsModal}
          detailModal={detailModal}
          eventId={eventId}
        />
      )}
      <div className="container-fluid">
        <div className="ag-theme-alpine" style={{ height: 650, width: "100%" }}>
          <div className="mb-2 d-flex flex-row align-items-center">
            <Button onClick={exportCurrentPage} className="download_report">
              Export current page
            </Button>
            <Button onClick={exportAll} className="download_report ms-2">
              Export all data to CSV
            </Button>
            <div className="ms-2 me-auto">
              <Button onClick={handleShowCharts} className="download_report">
                {!showCharts ? "Analytics Charts" : "Analytics Table"}
              </Button>
            </div>
            <Button className="ms-2 me-auto" onClick={handleResetSearch}>
              Clear search
            </Button>
          </div>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <RotatingLines width="64" strokeColor="grey" />
            </div>
          ) : !showCharts ? (
            <>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={60}
                headerHeight={60}
                alwaysShowHorizontalScroll={true}
                alwaysShowVerticalScroll={true}
                onGridReady={onGridReady}
              />

              {rowData.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  <Button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    Prev
                  </Button>

                  <span className="mx-3" style={{ lineHeight: "38px" }}>
                    Page {currentPage} of {Math.ceil(totalCount / itemsPerPage)}
                  </span>

                  <Button
                    disabled={
                      currentPage === Math.ceil(totalCount / itemsPerPage)
                    }
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: "0px 0px 0px 10px" }}>
              <h5>Analytics Charts</h5>
              <AnalyticsCharts />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReport;
